// The Lobbero IQ wire contract. Matches the backend responses in
// lobbero_be/src/CRUD/iq.js (renderSummary / degradedPayload / the entitlement
// endpoint, plus the four POST /iq/ask shapes documented above AskResponse).
// Nothing here is vendor-specific — the FE never sees a model id, a token count
// or a Gemini error class.
//
// NOT EVERYTHING IN THIS FILE IS AI OUTPUT. The on-duty block below is computed
// in SQL and injected server-side; it arrives on an IQ endpoint because that is
// where the shift window is resolved, not because a model produced it. Anything
// rendering it must stay off the aurora gradient.

export type IqStatus = 'ok' | 'follow_up' | 'incident';

// Words, not only hue (design_system §status): the pill text IS the state.
export const STATUS_LABEL: Record<IqStatus, string> = {
  ok: 'All clear',
  follow_up: 'Follow up',
  incident: 'Incident',
};

export interface IqSection {
  status: IqStatus;
  label: string;
  text: string;
  source_ids: string[]; // namespaced: 'v:8831' | 'd:412'
  /** true when the server's incident floor wrote this row (the model omitted or
   *  was blocked from describing an incident). Rendered WITHOUT the "✦ AI" chip. */
  system?: boolean;
}

export interface IqCounts {
  entries: number;
  visitor_entries: number;
  daily_entries: number;
  people_admitted: number;
  people_refused: number;
  emergency_headcount: number;
  deliveries_logged: number;
  denied_entries: number;
  incidents: number;
  follow_ups: number;
  by_kind: Record<string, number>;
  /* ---- WP-20. The parcel figures, which the backend has computed since
   * migration 042 and which the summary prompt dropped before the model until
   * shift-summary.v3. The stale comment that used to sit here said "Lobbero
   * records no pickup event" — it does, and has since 042.
   *
   * packages_pending is AS OF the end of the window, with NO lower bound: it
   * counts everything still on the shelf whatever day it arrived. NEVER render a
   * date range beside it. The other three ARE window facts. */
  packages_pending?: number;
  packages_logged?: number;
  packages_picked_up?: number;
  packages_returned?: number;
  /* ---- WP-16/WP-20. The exit picture, all three bounded on the ARRIVAL, so
   * each figure belongs to the shift that took the person in.
   *
   * exits_auto_closed is a DESK-PROCESS GAP, not a departure anybody saw. Never
   * label it "departures" in the UI. exits_desk_recorded is the observation. */
  exits_desk_recorded?: number;
  exits_auto_closed?: number;
  arrivals_still_open?: number;
}

/** One row of the raw chronological log. `people` are REAL, DETOKENISED names:
 *  the caller already holds dailylog:read AND visitorlog:read, so this is data
 *  they can fetch by hand. Tokenisation is a boundary control for what leaves the
 *  box to Google, not a display control. */
export interface IqEntry {
  /** 'v:8831' | 'd:412' | 'pa:<uuid>' | 'ph:<uuid>' — also the DOM anchor id.
   *  WP-20: a PARCEL contributes up to TWO entries, an arrival ('pa:') stamped
   *  when it was logged in and a handover ('ph:') stamped when it was collected.
   *  They are separate events at separate times, frequently on separate shifts,
   *  so they are separate rows and separate citations. */
  id: string;
  src: 'visitor_log' | 'daily_log' | 'package';
  /** number for the two log tables, uuid string for a parcel. */
  rowId: number | string;
  at: string; // ISO instant, UTC
  kind: string;
  subKind: string;
  location: string;
  people: string[];
  headcount: number | null;
  priority: 'Low' | 'Medium' | 'High' | null;
  approval: 'Yes' | 'No' | null;
  parking: 'Yes' | 'No' | null;
  preAuthorized: 'Yes' | 'No' | null;
  authorId: string | null; // rendered as "recorded by", never "written by"
  text: string[];
  severity: IqStatus;
  flaggedByKeyword?: boolean;
  /* ---- WP-16/WP-20. THE EXIT, on the arrival row it belongs to. ----
   * departedAt null means the desk has no departure record — which is NOT the
   * same as the person still being here, and must never be rendered as either
   * "left" or "still inside" without saying which.
   *
   * exitSource is the ONLY thing separating an observation from a cleanup, and
   * chk_vl_exit_machine (migration 059) makes it a database guarantee: 'desk'
   * always has a recorder, 'auto'/'backfill' never do. Render 'auto' and
   * 'backfill' as "auto-closed", never as "left at HH:MM". */
  departedAt?: string | null;
  exitSource?: 'desk' | 'auto' | 'backfill' | null;
  /* ---- WP-20. Present only on a parcel entry. ---- */
  parcelEvent?: 'arrival' | 'handover';
  parcelStatus?: string;
  parcelCount?: number;
}

export interface IqEntitlement {
  enabled: boolean;
  reason: string;
  trial: boolean;
  delinquent: boolean;
  can_buy: boolean;
}

export type DegradeReason =
  | 'disabled_globally'
  | 'tier_unattested'
  | 'capabilities_unverified'
  | 'free_tier_block'
  | 'tier_canary_free'
  | 'model_unverified'
  | 'busy'
  | 'timeout'
  | 'vendor_error'
  | 'rate_limited'
  | 'quota_daily'
  | 'invalid_json'
  | 'truncated'
  | 'refused'
  | 'safety_blocked'
  | 'spii_blocked'
  | 'citation_failed'
  | 'budget_exceeded'
  | 'empty'
  | 'quiet'
  | 'source_truncated'
  | 'source_moved'
  | 'token_map_unreadable';

export interface IqDegraded {
  reason: DegradeReason;
  detail?:
    | string
    | {
        total?: number;
        entry_limit?: number;
        retry_after_ms?: number;
        code?: string;
        spent_micros?: number;
        budget_micros?: number;
        resets_at?: string;
        can_buy?: boolean;
      };
}

/* ─────────────────────────  WHO WAS ON DUTY  ─────────────────────────
 *
 * The on-duty block (lobbero_be/src/iq/on-duty.js). NOT AI OUTPUT: the block is
 * computed in SQL and injected server-side, and the model is structurally
 * incapable of emitting a staff identity (on-duty.js:32-38 — a hallucinated name
 * against a staff roster is an accusation, not a wrong number). So whatever
 * renders it must NOT carry the aurora mark; that gradient means "a model wrote
 * this", and here no model did.
 */

/** How a person's span was established. `clocked` is their own statement (a row
 *  in `shifts`); `derived` is an inference from the rows they authored, which
 *  exists because A FORGOTTEN CLOCK-IN MUST NOT PRODUCE A HANDOVER CLAIMING
 *  NOBODY WAS ON DUTY while the log is full of that person's entries
 *  (on-duty.js:6-9). The UI must keep the two distinguishable. */
export type OnDutySource = 'clocked' | 'derived';

/** Block-level provenance — the honest summary of the list as a whole
 *  (on-duty.js:174-183). 'none' is a real, meaningful state: nobody clocked and
 *  nobody wrote anything. It must never be softened into a roster. */
export type OnDutyProvenance = 'none' | 'clocked' | 'derived' | 'mixed';

export interface OnDutyPerson {
  user_id: string;
  /** Decrypted server-side. '' when the name could not be resolved — expected,
   *  not an error (on-duty.js:207). */
  name: string;
  source: OnDutySource;
  from: string;
  /** NULL on an open-ended shift. NOT fabricated: an open shift matches every
   *  later window, so the renderer shows "still open" rather than inventing an
   *  end time (on-duty.js:123-126). */
  to: string | null;
  still_open: boolean;
  ended_retroactively: boolean;
  entry_count: number;
  edit_count: number;
}

/** Somebody who only EDITED rows in the window. Deliberately NOT placed on duty
 *  — an edit is evidence of authorship, not of presence (a manager correcting a
 *  name from home at 23:00). Surfaced separately so the reader can judge
 *  (on-duty.js:149-155). */
export interface OnDutyEditOnly {
  user_id: string;
  name: string;
  edit_count: number;
}

export interface IqOnDuty {
  provenance: OnDutyProvenance;
  people: OnDutyPerson[];
  edit_only: OnDutyEditOnly[];
}

export interface ShiftSummaryResponse {
  window: {
    building_id?: number;
    from: string;
    to: string;
    tz: string;
    entry_limit?: number;
    /**
     * TRUE when this window is the caller's OWN SHIFT, read from the `shifts`
     * table, rather than the 07/15/23 anchor grid.
     *
     * It matters to the reader: "nothing was logged" means something very
     * different about the eight minutes you just worked than about the last
     * closed eight-hour block — and the anchor default is exactly what produced
     * an "All clear" handover over a shift that had three entries and two
     * parcels in it.
     */
    shift_anchored?: boolean;
    /** The shift has not been clocked out, so `to` is a rounded now(). */
    shift_open?: boolean;
    /** A forgotten clock-out: the span was cut to the most recent 26 hours. */
    shift_clamped?: boolean;
  };
  counts: IqCounts | null;
  entries: IqEntry[];
  truncated: boolean;
  total?: number;
  summary: { headline: string; sections: IqSection[] } | null;
  /**
   * WHO WROTE THE SUMMARY ABOVE.
   *
   * Absent (or 'model') means the assistant wrote it. 'derived' means it did
   * not — the vendor failed, was blocked, ran out of budget or was already busy
   * — and the backend composed the handover from the rows it had already
   * retrieved instead of answering with nothing. Every section on a derived
   * summary also carries `system: true`, so they render without the AI chip;
   * this field is what lets the CARD say so once, at the top, rather than
   * leaving the reader to infer it from the absence of chips.
   */
  summary_source?: 'model' | 'derived';
  floor_sections?: IqSection[];
  cached: boolean;
  tier?: 'standard' | 'economy';
  partial?: boolean;
  degraded?: IqDegraded | null;
  /** OPTIONAL BECAUSE THE KEY IS ABSENT, not null, for a caller without
   *  `user:read` (lobbero_be/src/CRUD/iq.js:230-250). A concierge simply never
   *  receives it — the block is not built and hidden client-side, because a
   *  per-person activity record must not travel to a client that may not see
   *  it. Present on EVERY other path: fresh, cached, degraded, quiet, empty. */
  on_duty?: IqOnDuty;
}

/* ───────────────────────────  ASK  ───────────────────────────
 *
 * POST /iq/ask emits FOUR shapes off one route, and the FIRST FIELD TO READ IS
 * `mode` (lobbero_be/src/CRUD/iq.js):
 *
 *   1. REFUSAL   (iq.js:694) supported:false, no mode. NOTE from/to are EMPTY
 *      STRINGS, not ISO — every date formatter here must keep its `if (!iso)`
 *      guard or it prints "Invalid Date".
 *   2. PLAN/COUNT(iq.js:849) supported:true, mode:'count' + metric (WP-26; it
 *      carried no mode at all before, so it was indistinguishable from a refusal
 *      except by the fields it happened to have). A count computed in SQL:
 *      EXACT. This is the only shape the old interface described.
 *   3. LIST      (iq.js:808) mode:'list' + metric + rows + org_wide +
 *      window_source. Exact, and the rows ARE the answer — no model was asked
 *      to restate them (iq.js:795-804).
 *   4. SEMANTIC  (iq.js:608) mode:'semantic' + found. RETRIEVED SUBSET, never a
 *      total; the server hard-codes source_summary.sampled = true for exactly
 *      that reason (iq.js:612-614).
 *
 * EVERY NEW FIELD IS OPTIONAL, and that is not laziness: shapes 1 and 2 carry
 * none of them, so a required field would be a lie the compiler cannot catch —
 * it would fail at runtime on `res.rows.map`.
 */

/** WP-19 adds a FIFTH shape: mode:'facts' + metric + facts. One structured
 *  object, not a list with a single row — an account snapshot has a dozen named
 *  fields and shipping it as rows[0] would invite a one-row table of a dozen
 *  columns. `answer` still carries the whole thing as text, so a client that
 *  does not know the mode is never left with nothing. */
/** WP-26 adds two more. `count` is not a new SHAPE — it is the plan/count answer
 *  that always existed, which simply carried no `mode` and could therefore not
 *  be told apart from a refusal by anything but the absence of fields. `compound`
 *  is WP-27's multi-part answer. Both are listed so the union describes what the
 *  server sends; iq-answer.tsx renders anything it does not recognise as the
 *  plain `answer` text, which is the whole answer for both of them. */
export type AskMode = 'list' | 'semantic' | 'facts' | 'count' | 'compound';

/** The closed set of list metrics (lobbero_be/src/iq/ask-query.js LIST_METRICS
 *  and FACT_METRICS). Every one of these arrives with `rows`, EXCEPT the two
 *  fact metrics, which arrive with `facts`. */
export type AskListMetric =
  /** THE ONE MEMBER THAT IS NOT A LIST METRIC SERVER-SIDE, and it is here
   *  because this union is what `metric` on the RESPONSE is typed as — not what
   *  LIST_METRICS holds in ask-query.js. list_recent answers on mode:'count'
   *  (it takes a phrasing call), and since the 2026-08-13 pass it carries
   *  AskRecentRow[] in `rows` alongside the sentence. Leaving it out typed the
   *  metric it actually sends as impossible. */
  | 'list_recent'
  | 'list_packages'
  | 'list_occupants'
  | 'locate_person'
  | 'list_buildings'
  | 'list_units'
  | 'list_staff'
  /** WP-20. Rows are visitor arrivals with a departure; each carries `observed`,
   *  which is false for an auto-closed record. A UI that drops that flag turns a
   *  cleanup into a claim that somebody was seen leaving. */
  | 'list_departures'
  | 'account_snapshot'
  /** WP-20. A FACT metric, not a list, and the shape IS the rule: it arrives as
   *  `facts.residents` and `facts.visitorsInside`, two independently counted
   *  halves. Never sum them and never render one number. */
  | 'who_is_inside'
  | 'capabilities';

/** Where the window came from. 'shift' and 'last_12h' are DERIVED from the
 *  asker's own clock rather than from words in the question
 *  (ask-query.js:122-129, :191), so the UI says which — "this shift" means a
 *  different span for a different person. 'as_of' means there was no window at
 *  all: an account snapshot is a statement about NOW, and printing a date range
 *  under it would describe a measurement that never happened. */
export type AskWindowSource = 'range' | 'shift' | 'last_12h' | 'as_of';

/** Parcel rows — lobbero_be/src/iq/ask-facts.js:88-108. */
export interface AskPackageRow {
  id: string;
  buildingId: number;
  building: string;
  unit: string;
  carrier: string;
  count: number;
  shelf: string;
  status: string;
  loggedAt: string;
  pickedUpAt: string | null;
  recipient: string;
  releasedTo: string;
  /** Provenance travels WITH the fact (041/042): a typed name and a name matched
   *  to a resident are different levels of certainty, and flattening them claims
   *  more than the row knows (ask-facts.js:103-107). */
  recipientResolution: string;
  releasedToResolution: string;
}

/** THE RECENT-ENTRY LOG — lobbero_be/src/CRUD/iq.js, the list_recent branch.
 *
 *  The only row shape that arrives on mode:'count'. "Who entered today" plans to
 *  list_recent, which is a COUNT metric with a phrasing call, so the response
 *  keeps mode:'count' and carries these alongside — a client that does not know
 *  the field renders `answer` exactly as it did before. */
export interface AskRecentRow {
  /** The citable token ('v:8831', 'd:412', 'pa:…', 'ph:…'). Key only. */
  id: string;
  at: string;
  kind: string;
  unit: string | null;
  /** REAL names, decrypted server-side. ask-pii declares list_recent as
   *  returning visitor_names under the route's own log-read gate. */
  people: string[];
  note: string | null;
  severity: string;
  /** Still inside (a person) or still open (a building activity). null for a
   *  parcel event, which has no open state — its two events are separate rows. */
  open: boolean | null;
}

/** Current occupants — ask-facts.js:137-152. Current only (moved_out IS NULL). */
export interface AskOccupantRow {
  id: string;
  buildingId: number;
  building: string;
  unit: string;
  name: string;
  relationship: string;
  isPrimary: boolean;
  movedIn: string | null;
}

/** "Where does X live?" — ask-facts.js:196-221.
 *
 *  `building` IS DELIBERATELY '' AND `buildingId` NULL FOR A BUILDING-SCOPED
 *  CALLER, and it is never assembled server-side rather than redacted after the
 *  fact (ask-facts.js:211-215). A concierge already knows which building they
 *  are standing in, and the answer must not become a way to learn that a person
 *  exists somewhere else in the organisation.
 *
 *  DO NOT "FIX" THIS BY SHOWING AN EMPTY BUILDING COLUMN. A blank cell under a
 *  Building header reads as "something was hidden from you". Render the unit
 *  alone; the column appears only when `org_wide` is true. */
export interface AskLocatedPersonRow {
  name: string;
  unit: string;
  building: string;
  buildingId: number | null;
  current: boolean;
  relationship: string;
  isPrimary: boolean;
}

/** Buildings in scope — ask-facts.js listBuildings. */
export interface AskBuildingRow {
  id: number;
  building: string;
  portfolio: string;
  city: string;
  status: string;
  timezone: string;
  units: number;
  residents: number;
  parcelsWaiting: number;
}

/** The unit inventory — ask-facts.js listUnits. `occupied` is derived from OPEN
 *  OCCUPANCIES, never from `status`: status says whether the unit is part of the
 *  inventory, an occupancy says whether anybody lives in it. */
export interface AskUnitRow {
  id: number;
  buildingId: number;
  building: string;
  unit: string;
  name: string;
  kind: string;
  status: string;
  floor: string;
  residents: number;
  occupied: boolean;
}

/** Staff and the buildings they cover — ask-facts.js listStaff. `buildings` is
 *  already filtered to what the ASKER can see, so a short list means "of your
 *  buildings, these", never "this is their whole job". `allBuildings` is how an
 *  owner is distinguished from somebody with no grant at all. */
export interface AskStaffRow {
  id: string;
  name: string;
  role: string;
  allBuildings: boolean;
  buildings: string[];
}

/** The account snapshot — ask-facts.js accountSnapshot.
 *
 *  `plan` IS ABSENT, NOT NULLED, for a caller without billing:manage. Do not
 *  render a placeholder for it: a blank where a number would be invites the
 *  question the omission exists to avoid. */
export interface AskAccountFacts {
  orgWide: boolean;
  portfolios: number;
  buildings: number;
  units: number;
  unitsActive: number;
  unitsVacant: number;
  unitsOccupied: number;
  residents: number;
  parcelsWaiting: number;
  shiftsOpen: number;
  /** null when the organisation could not be bounded — never a guess. */
  staff: number | null;
  /** 'organisation' or 'buildings' — WHICH staff number this is. The two are
   *  different facts and the label must follow the basis. */
  staffBasis: string;
  plan?: {
    code: string;
    name: string;
    priceCents: number;
    status: string;
    trial: boolean;
    delinquent: boolean;
    iqEnabled: boolean;
    iqReason: string;
    unitsBought: number;
    unitsUsed: number;
    unitsRemaining: number;
  };
}

/** The discriminant lives on the RESPONSE (`metric`), not on the row, so a
 *  consumer narrows once at the top of the render and not per item. */
export type AskRow =
  | AskPackageRow
  | AskOccupantRow
  | AskLocatedPersonRow
  | AskBuildingRow
  | AskUnitRow
  | AskStaffRow
  | AskRecentRow;

/** WHAT THE ANSWER WAS COMPUTED OVER (owner decision D-20). Present on EVERY
 *  answer shape, refusals included, because "42" reads as a total and a scoped
 *  reader has no way to tell 42-of-one-building from 42-of-everything.
 *  `label` is the same sentence the server already put inside `answer`, so a
 *  client can badge it without reformatting prose. */
export interface AskScope {
  org_wide: boolean;
  buildings: number;
  /** Non-empty only when the QUESTION named one building. */
  building: string;
  label: string;
}

export interface AskResponse {
  /** THE CLOCK THE ANSWER IS ON (R-T12): the building's for a concierge, the
   *  reader's device for an owner or a manager — "today", "yesterday" and every
   *  row's time included. Both are ABSENT on a refusal that fires before the
   *  question is planned; such an answer shows no time and gets no clock note. */
  tz?: string;
  tz_source?: 'viewer' | 'building' | 'fallback';
  answer: string;
  supported: boolean;
  unsupported_reason?: string;
  source_ids: string[];
  source_summary: {
    count: number;
    from: string;
    to: string;
    /**
     * TRUE when the entries were RETRIEVED — ranked by relevance, capped, a
     * subset of the period. FALSE when the window was short enough to read
     * chronologically and in full, which is now the case whenever the question
     * names a narrow period ("today", "yesterday", "on my shift"). The caveat
     * under the answer depends on this: telling somebody their answer came from
     * a selection when it did not sends them to search a log they have already
     * been shown all of.
     */
    sampled: boolean;
    /** "today" / "yesterday" / "your shift" — the period the QUESTION named. */
    period_label?: string;
  };

  /** Absent on the refusal and plan/count shapes — see the block comment above. */
  mode?: AskMode;
  /** list mode only. */
  metric?: AskListMetric;
  /** list mode only. The structured rows, so the client renders a table instead
   *  of re-parsing `answer` (iq.js:813-814). */
  rows?: AskRow[];
  /** list mode only. TRUE means the answer spans the whole organisation, which
   *  is what makes a Building column meaningful — and honest (iq.js:815). */
  org_wide?: boolean;
  /** list mode only. */
  window_source?: AskWindowSource;
  /** semantic mode only. FALSE is not "nothing happened" — it is "the entries
   *  that were searched do not mention it" (ask-semantic.js:196-198). The two
   *  are different claims and the UI must not conflate them. */
  found?: boolean;

  /** facts mode only (metric 'account_snapshot'). */
  facts?: AskAccountFacts;
  /** TRUE when the answer has no time span at all — it is a statement about NOW.
   *  Never print a date range beside one. */
  as_of?: boolean;
  /** Every shape carries this, refusals included. See AskScope. */
  scope?: AskScope;
}

export interface IqUsage {
  spent_micros: number;
  budget_micros: number;
  pct: number;
  warn: boolean;
  over: boolean;
  trial: boolean;
  delinquent: boolean;
  by_feature: Record<string, number>;
  month_start: string;
}
